import React, { useMemo } from "react";
import DOMPurify from "dompurify";

function MathText({ text }) {
  const renderMathInText = (text) => {
    if (!text || typeof text !== "string") return "";

    const htmlTagRegex = /<\/?[a-z][\s\S]*>/i;
    if (htmlTagRegex.test(text)) {
      return text;
    }

    let processedText = text.replace(/\\\\/g, "\\").trim();

    const parts = [];
    let lastIndex = 0;

    // Matches: \[...\], $$...$$, \(...\), $...$
    const regex =
      /\\\[([\s\S]+?)\\\]|\$\$([\s\S]+?)\$\$|\\\(([\s\S]+?)\\\)|\$([^\$\n]+?)\$/g;

    let match;
    while ((match = regex.exec(processedText)) !== null) {
      if (match.index > lastIndex) {
        parts.push({
          type: "text",
          content: processedText.slice(lastIndex, match.index),
        });
      }

      const mathContent = match[1] || match[2] || match[3] || match[4];
      const isDisplayMode = !!(match[1] || match[2]);

      parts.push({
        type: "math",
        content: mathContent,
        displayMode: isDisplayMode,
      });

      lastIndex = regex.lastIndex;
    }

    if (lastIndex < processedText.length) {
      parts.push({
        type: "text",
        content: processedText.slice(lastIndex),
      });
    }

    return parts
      .map((part) => {
        if (part.type === "text") {
          return part.content;
        } else {
          try {
            return window.katex.renderToString(part.content, {
              throwOnError: false,
              displayMode: part.displayMode,
              strict: false,
              trust: false,
            });
          } catch (error) {
            console.error("KaTeX rendering error:", error);
            return part.displayMode
              ? `\\[${part.content}\\]`
              : `\\(${part.content}\\)`;
          }
        }
      })
      .join("");
  };

  const html = useMemo(() => renderMathInText(text), [text]);
  const safeHtml = useMemo(
    () =>
      DOMPurify.sanitize(html, {
        USE_PROFILES: { html: true, svg: true, svgFilters: true },
        ADD_TAGS: [
          "math",
          "mrow",
          "msqrt",
          "mfrac",
          "mi",
          "mn",
          "mo",
          "msup",
          "msub",
          "msubsup",
          "mtext",
        ],
        ADD_ATTR: [
          "xmlns",
          "width",
          "height",
          "viewBox",
          "preserveAspectRatio",
          "style",
        ],
      }),
    [html]
  );

  return <span dangerouslySetInnerHTML={{ __html: safeHtml }} />;
}

export default MathText;
